import { CaretDown, CircleNotch, type Icon } from "@phosphor-icons/react";
import {
  type ButtonHTMLAttributes,
  type ForwardedRef,
  type FunctionComponent,
  forwardRef,
  type ReactNode,
  type SVGProps,
} from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import { cn, getWhitelabelCustomization } from "common/helpers/utils";

import { type ButtonVariantType, buttonVariants } from "../ui/Button";

import type { VariantProps } from "class-variance-authority";

type SplitButtonVariant = Extract<
  ButtonVariantType,
  "default-black" | "default-purple" | "secondary-black" | "secondary-purple"
>;

export type SplitButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  Omit<VariantProps<typeof buttonVariants>, "intent" | "variant"> & {
    variant?: SplitButtonVariant;
    leftIcon?: ReactNode;
    isLoading?: boolean;
    dropdownMenuItems: {
      label: string;
      icon: FunctionComponent<SVGProps<SVGSVGElement>> | Icon;
      onSelect: () => void;
      disabled?: boolean;
    }[];
  };

const dividerColorClass: Record<SplitButtonVariant, string> = {
  "default-black": "before:bg-black-700",
  "default-purple": "before:bg-purple-400",
  "secondary-black": "before:bg-black-200",
  "secondary-purple": "before:bg-purple-200",
};

function SplitButton(
  {
    className,
    variant = "default-black",
    size = "md",
    children,
    leftIcon = null,
    disabled,
    isLoading = false,
    dropdownMenuItems,
    ...props
  }: SplitButtonProps,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  if (getWhitelabelCustomization() && variant === "secondary-purple") {
    variant = "secondary-black";
  }

  const loadingComponent = <CircleNotch className="animate-spin" />;

  return (
    <div className="flex">
      <button
        type="button"
        className={buttonVariants({
          variant,
          size,
          intent: "default",
          hasLeftIcon: !!leftIcon,
          hasRightIcon: true,
          hasWhitelabelColor: !!getWhitelabelCustomization()?.primary_color,
          className: "rounded-r-none",
        })}
        ref={ref}
        {...props}
        disabled={isLoading || disabled}
      >
        {isLoading && leftIcon ? loadingComponent : leftIcon}
        {children}
      </button>

      <DropdownMenu>
        <DropdownMenuTrigger
          className={cn(
            buttonVariants({
              variant,
              size,
              intent: "iconOnly",
              hasWhitelabelColor: !!getWhitelabelCustomization()?.primary_color,
              className: [
                "rounded-l-none relative group/dropdown-button",
                "before:absolute before:left-0 before:h-5 before:w-px disabled:before:bg-black-400",
                dividerColorClass[variant],
              ],
            }),
          )}
          disabled={isLoading || disabled}
        >
          <CaretDown className="transition-transform group-data-[state=open]/dropdown-button:rotate-180" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuList>
            {dropdownMenuItems.map(
              ({ label, icon: Icon, onSelect, disabled }) => (
                <DropdownMenuItem
                  onSelect={onSelect}
                  key={label}
                  disabled={disabled}
                >
                  <Icon />
                  {label}
                </DropdownMenuItem>
              ),
            )}
          </DropdownMenuList>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

SplitButton.displayName = "SplitButton";

const SplitButtonWithRef = forwardRef(SplitButton);

export { SplitButtonWithRef as SplitButton };
